import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useUI } from "@/context/AppContext";
import { fieldCls, labelCls, errCls } from "@/components/formStyles";

export default function ConsultingForm() {
  const ui = useUI();
  const MISSIONS = [
    ["Entrée sur le marché ivoirien/ouest-africain", ui.mission_market],
    ["Accompagnement à l'export", ui.mission_export],
    ["Structuration d'accords commerciaux", ui.mission_deals],
    ["Veille réglementaire", ui.mission_watch],
    ["Autre", ui.mission_other],
  ];
  const [form, setForm] = useState({
    full_name: "", company: "", country: "", email: "", phone: "",
    job_title: "",
    mission_type: "", description: "", website: "", math_answer: "",
    attestation: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [captcha, setCaptcha] = useState({ a: null, b: null, token: "" });

  const loadCaptcha = useCallback(async () => {
    try {
      const { data } = await api.get("/forms/captcha");
      setCaptcha(data);
    } catch { setCaptcha({ a: null, b: null, token: "" }); }
  }, []);
  useEffect(() => { loadCaptcha(); }, [loadCaptcha]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = ui.required;
    if (!form.job_title.trim()) e.job_title = ui.required;
    if (!form.company.trim()) e.company = ui.required;
    if (!form.country.trim()) e.country = ui.required;
    if (!form.email.trim()) e.email = ui.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = ui.invalid_email;
    if (!form.mission_type) e.mission_type = ui.select_one;
    if (!form.description.trim()) e.description = ui.required;
    if (captcha.a == null || parseInt(form.math_answer, 10) !== captcha.a + captcha.b) e.math_answer = ui.math_wrong;
    if (!form.attestation) e.attestation = ui.attestation_error;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/forms/consulting", {
        ...form,
        captcha_token: captcha.token,
        captcha_answer: parseInt(form.math_answer, 10),
      });
      setDone(true);
      toast.success(ui.success);
    } catch (err) {
      toast.error(err?.response?.data?.detail || ui.error);
      loadCaptcha();
      setForm((f) => ({ ...f, math_answer: "" }));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-[#1CADE4]/30 bg-white p-10 text-center" data-testid="consulting-form-success">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#1CADE4] text-2xl text-white">✓</div>
        <p className="font-montserrat text-xl font-bold text-neutral-900">{ui.success}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="consulting-form" noValidate>
      <input
        type="text" tabIndex={-1} autoComplete="off"
        value={form.website} onChange={(e) => set("website", e.target.value)}
        className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" data-testid="consulting-honeypot"
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls}>{ui.full_name} *</label>
          <input className={fieldCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="consulting-full-name" />
          {errors.full_name && <p className={errCls}>{errors.full_name}</p>}
        </div>
        <div>
          <label className={labelCls}>{ui.job_title} *</label>
          <input className={fieldCls} value={form.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="consulting-job-title" />
          {errors.job_title && <p className={errCls}>{errors.job_title}</p>}
        </div>
        <div>
          <label className={labelCls}>{ui.organization} *</label>
          <input className={fieldCls} value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="consulting-company" />
          {errors.company && <p className={errCls}>{errors.company}</p>}
        </div>
        <div>
          <label className={labelCls}>{ui.country} *</label>
          <input className={fieldCls} value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="consulting-country" />
          {errors.country && <p className={errCls}>{errors.country}</p>}
        </div>
        <div>
          <label className={labelCls}>{ui.email} *</label>
          <input type="email" className={fieldCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="consulting-email" />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>{ui.phone}</label>
          <input className={fieldCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="consulting-phone" />
        </div>
      </div>

      <div>
        <label className={labelCls}>{ui.mission_type} *</label>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {MISSIONS.map(([val, lbl]) => (
            <label key={val} className={`cursor-pointer rounded-xl border px-4 py-3 font-poppins text-sm transition-colors ${form.mission_type === val ? "border-[#1CADE4] bg-[#1CADE4]/10 text-[#0f7ba3]" : "border-black/10 text-neutral-600 hover:border-[#1CADE4]"}`}>
              <input type="radio" name="mission" className="sr-only" checked={form.mission_type === val} onChange={() => set("mission_type", val)} data-testid={`consulting-mission-${val.slice(0, 8)}`} />
              {lbl}
            </label>
          ))}
        </div>
        {errors.mission_type && <p className={errCls}>{errors.mission_type}</p>}
      </div>

      <div>
        <label className={labelCls}>{ui.project_desc} *</label>
        <textarea rows={4} className={fieldCls} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="consulting-description" />
        {errors.description && <p className={errCls}>{errors.description}</p>}
      </div>

      <div>
        <label className={labelCls}>{ui.security_q} : {captcha.a != null ? `${captcha.a} + ${captcha.b}` : "…"} = ? *</label>
        <input type="number" className={`${fieldCls} max-w-[140px]`} value={form.math_answer} onChange={(e) => set("math_answer", e.target.value)} data-testid="consulting-math" />
        {errors.math_answer && <p className={errCls}>{errors.math_answer}</p>}
      </div>

      <div>
        <label className="flex cursor-pointer items-start gap-3 font-poppins text-sm text-neutral-700" data-testid="consulting-attestation-label">
          <input
            type="checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-[#1CADE4]"
            checked={form.attestation} onChange={(e) => set("attestation", e.target.checked)}
            data-testid="consulting-attestation"
          />
          <span>{ui.attestation} *</span>
        </label>
        {errors.attestation && <p className={errCls}>{errors.attestation}</p>}
      </div>

      <button
        type="submit" disabled={submitting} data-testid="consulting-submit"
        className="w-full rounded-full bg-[#1CADE4] px-8 py-4 font-poppins text-base font-semibold text-white shadow-md transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 sm:w-auto"
      >
        {submitting ? ui.sending : ui.book_meeting}
      </button>
    </form>
  );
}
